export const SITE_URL = "https://www.hotple.com";

export const DETAIL_PATHS_WITHOUT_QUERY = Object.freeze([
  "/post",
  "/play",
  "/place",
  "/profile",
]);

const ID_FALLBACK_KEYS = [
  "publicId",
  "placeUniqueNumber",
  "id",
  "_id",
  "contentid",
  "contentId",
];

const PLACE_PUBLIC_ID_KEYS = ["publicId", "placeUniqueNumber"];
const PLACE_OBJECT_ID_DETAIL_PATH_PATTERN = /^\/place\/[a-f0-9]{24}$/i;
const KOREAN_REGION_SUFFIX_PATTERN =
  /(?:특별자치시|특별자치도|광역시|특별시|자치시|자치도|시|군|구|읍|면|동|리)$/u;
const REGION_NAMES_WITH_SUFFIX_SYLLABLE = new Set(["대구"]);
const HANGUL_PATTERN = /[\uAC00-\uD7A3]/;
const HANGUL_START_CODE = 0xac00;
const HANGUL_END_CODE = 0xd7a3;
const HANGUL_MEDIAL_COUNT = 21;
const HANGUL_FINAL_COUNT = 28;
const HANGUL_INITIAL_ROMANIZATION = [
  "g", "kk", "n", "d", "tt", "r", "m", "b", "pp", "s",
  "ss", "", "j", "jj", "ch", "k", "t", "p", "h",
];
const HANGUL_MEDIAL_ROMANIZATION = [
  "a", "ae", "ya", "yae", "eo", "e", "yeo", "ye", "o", "wa",
  "wae", "oe", "yo", "u", "wo", "we", "wi", "yu", "eu", "ui", "i",
];
const HANGUL_FINAL_ROMANIZATION = [
  "", "k", "k", "ks", "n", "nj", "nh", "t", "l", "lk",
  "lm", "lb", "ls", "lt", "lp", "lh", "m", "p", "ps", "t",
  "t", "ng", "t", "t", "k", "t", "p", "h",
];
const ROMANIZATION_OVERRIDES = [
  [/소플러스/g, "soplus"],
];

export const stripQueryAndHash = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).split("?")[0].split("#")[0];
};

export const stripQueryFromUrl = (value) => {
  if (!value || typeof value !== "string") return value;

  try {
    const url = new URL(value, SITE_URL);
    url.search = "";
    url.hash = "";
    return url.toString();
  } catch {
    return stripQueryAndHash(value);
  }
};

export const normalizeRouteSegment = (value, { lowercase = true } = {}) => {
  if (value === null || value === undefined) return "";

  const rawValue = stripQueryAndHash(String(value).trim());
  let decodedValue = rawValue;

  try {
    decodedValue = decodeURIComponent(rawValue);
  } catch (_) {
    decodedValue = rawValue;
  }

  const normalized = decodedValue.trim().replace(/^\/+/, "").replace(/^@+/, "");
  const segment = normalized.split("/")[0] || "";
  return lowercase ? segment.toLowerCase() : segment;
};

export const normalizeIdSegment = (value) =>
  normalizeRouteSegment(value, { lowercase: false });

export const createSlug = (value, { fallback = "place" } = {}) => {
  const text = Array.isArray(value) ? value.filter(Boolean).join(" ") : value;

  const slug = String(text || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .normalize("NFC")
    .replace(/&/g, " and ")
    .replace(/['\u2019`]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\uAC00-\uD7A3\u3131-\u318E]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");

  return slug || fallback;
};

const applyRomanizationOverrides = (value) =>
  ROMANIZATION_OVERRIDES.reduce(
    (text, [pattern, replacement]) => text.replace(pattern, replacement),
    String(value || "")
  );

const romanizeHangulSyllable = (char) => {
  const code = char.charCodeAt(0);
  if (code < HANGUL_START_CODE || code > HANGUL_END_CODE) return char;

  const offset = code - HANGUL_START_CODE;
  const initialIndex = Math.floor(offset / (HANGUL_MEDIAL_COUNT * HANGUL_FINAL_COUNT));
  const medialIndex = Math.floor((offset % (HANGUL_MEDIAL_COUNT * HANGUL_FINAL_COUNT)) / HANGUL_FINAL_COUNT);
  const finalIndex = offset % HANGUL_FINAL_COUNT;

  return [
    HANGUL_INITIAL_ROMANIZATION[initialIndex],
    HANGUL_MEDIAL_ROMANIZATION[medialIndex],
    HANGUL_FINAL_ROMANIZATION[finalIndex],
  ].join("");
};

export const romanizeHangul = (value) => {
  const text = String(value || "");
  if (!HANGUL_PATTERN.test(text)) return text;

  return Array.from(applyRomanizationOverrides(text))
    .map(romanizeHangulSyllable)
    .join("");
};

export const createAsciiSlug = (value, { fallback = "place" } = {}) => {
  const text = Array.isArray(value) ? value.filter(Boolean).join(" ") : value;
  const slug = romanizeHangul(text)
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .normalize("NFC")
    .replace(/&/g, " ")
    .replace(/['\u2019`]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");

  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) ? slug : fallback;
};

const MAX_PLACE_SLUG_LENGTH = 100;
const ADDRESS_TOKEN_SEPARATOR_PATTERN = /[\s,()［］\[\]{}]+/u;
const NEIGHBORHOOD_TOKEN_PATTERN = /(?:동(?:\d+가)?|읍|면|리)$/u;
const CITY_DISTRICT_TOKEN_PATTERN = /(?:시|군|구)$/u;
const BRANCH_NAME_TOKEN_PATTERN =
  /(?:본점|직영점|지점|분점|호점|DT점|dt점|드라이브스루점|센터|점)$/u;
const FRANCHISE_BRANDS = [
  "스타벅스",
  "도미노피자",
  "맥도날드",
  "버거킹",
  "롯데리아",
  "이삭토스트",
  "컴포즈커피",
  "메가MGC커피",
  "메가커피",
  "빽다방",
  "투썸플레이스",
  "파리바게뜨",
  "뚜레쥬르",
  "KFC",
  "배스킨라빈스",
  "BBQ",
  "BHC",
  "교촌치킨",
  "굽네치킨",
  "네네치킨",
  "피자헛",
  "미스터피자",
  "파파존스",
  "이디야커피",
  "서브웨이",
  "맘스터치",
];
const CATEGORY_RULES = [
  ["돼지갈비", "갈비"],
  ["소갈비", "갈비"],
  ["갈비", "갈비"],
  ["타코", "타코"],
  ["피자", "피자"],
  ["치킨", "치킨"],
  ["족발", "족발"],
  ["보쌈", "보쌈"],
  ["삼겹살", "삼겹살"],
  ["국밥", "국밥"],
  ["냉면", "냉면"],
  ["라멘", "라멘"],
  ["라면", "라면"],
  ["초밥", "초밥"],
  ["스시", "스시"],
  ["파스타", "파스타"],
  ["햄버거", "햄버거"],
  ["버거", "버거"],
  ["샤브샤브", "샤브샤브"],
  ["떡볶이", "떡볶이"],
  ["분식", "분식"],
  ["베이커리", "베이커리"],
  ["디저트", "디저트"],
  ["커피", "커피"],
  ["카페", "카페"],
  ["이자카야", "이자카야"],
  ["야키토리", "이자카야"],
  ["사케", "이자카야"],
  ["포장마차", "포장마차"],
  ["포차", "포장마차"],
  ["생맥주", "생맥주집"],
  ["맥주", "생맥주집"],
  ["호프", "호프"],
  ["펍", "펍"],
  ["pub", "펍"],
  ["와인바", "와인바"],
  ["와인", "와인바"],
  ["칵테일바", "칵테일바"],
  ["칵테일", "칵테일바"],
  ["위스키바", "위스키바"],
  ["위스키", "위스키바"],
  ["전통주점", "전통주점"],
  ["막걸리", "전통주점"],
  ["요리주점", "요리주점"],
  ["가맥", "가맥"],
  ["한식", "한식"],
  ["중식", "중식"],
  ["일식", "일식"],
  ["양식", "양식"],
  ["술집", "술집"],
  ["바", "바"],
];
const SAFE_CATEGORY_ALIASES = new Map([
  ["소고기구이", "소고기"],
  ["돼지고기구이", "돼지고기"],
  ["일식당", "일식"],
  ["중식당", "중식"],
  ["한식당", "한식"],
  ["호프집", "호프"],
  ["맥주집", "생맥주집"],
  ["생맥주집", "생맥주집"],
  ["포차", "포장마차"],
  ["이자카야", "이자카야"],
  ["펍", "펍"],
  ["pub", "펍"],
  ["와인바", "와인바"],
  ["칵테일바", "칵테일바"],
  ["위스키바", "위스키바"],
  ["전통주점", "전통주점"],
  ["요리주점", "요리주점"],
  ["가맥", "가맥"],
]);

const normalizePlaceNameForSlug = (value) =>
  String(value || "")
    .normalize("NFC")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .join(" ");

const normalizeKoreanToken = (value) =>
  String(value || "")
    .normalize("NFC")
    .replace(/[^\p{L}\p{N}\uAC00-\uD7A3\u3131-\u318E]+/gu, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");

const truncatePlaceSlug = (value) =>
  String(value || "")
    .slice(0, MAX_PLACE_SLUG_LENGTH)
    .replace(/^-+|-+$/g, "");

const createKoreanPlaceSlug = (value, { fallback = "place" } = {}) => {
  const text = Array.isArray(value) ? value.filter(Boolean).join(" ") : value;
  const slug = truncatePlaceSlug(
    String(text || "")
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .normalize("NFC")
      .replace(/&/g, " and ")
      .replace(/['\u2019`]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9\uAC00-\uD7A3\u3131-\u318E]+/g, "-")
      .replace(/-{2,}/g, "-")
      .replace(/^-+|-+$/g, "")
  );

  return slug || fallback;
};

const compactMeaning = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[\s\-_]+/g, "")
    .replace(/(?:특별자치시|특별자치도|광역시|특별시|자치시|자치도|직영점|본점|지점|분점|호점|시|군|구|읍|면|동|리|점)$/u, "");

export const getPlaceBrandNameForSlug = (placeOrName) => {
  const name = normalizePlaceNameForSlug(
    typeof placeOrName === "string"
      ? placeOrName
      : placeOrName?.name || placeOrName?.title || ""
  );
  if (!name) return "";

  const tokens = name.split(/\s+/).filter(Boolean);
  if (tokens.length < 2) return name;

  const lastToken = tokens[tokens.length - 1];
  if (lastToken.length <= 16 && BRANCH_NAME_TOKEN_PATTERN.test(lastToken)) {
    return tokens.slice(0, -1).join(" ");
  }

  return name;
};

const isLikelyFranchisePlace = (place = {}) => {
  const brand = getPlaceBrandNameForSlug(place);
  const haystack = compactMeaning([brand, place?.name, place?.title].filter(Boolean).join(" "));
  return FRANCHISE_BRANDS.some((brandName) =>
    haystack.includes(compactMeaning(brandName))
  );
};

const getAddressTokens = (place = {}) =>
  [
    place.address,
    place.old_address,
    place.addressInformation,
    place.city,
    place.state,
  ]
    .filter(Boolean)
    .flatMap((value) => String(value).split(ADDRESS_TOKEN_SEPARATOR_PATTERN))
    .map((token) => normalizeKoreanToken(token))
    .filter(Boolean);

const getPlaceRegionParts = (place = {}) => {
  const tokens = getAddressTokens(place);
  const neighborhood = tokens.find((token) => NEIGHBORHOOD_TOKEN_PATTERN.test(token));
  const cityDistrict = tokens.find(
    (token) =>
      CITY_DISTRICT_TOKEN_PATTERN.test(token) &&
      token !== neighborhood &&
      !REGION_NAMES_WITH_SUFFIX_SYLLABLE.has(token)
  );

  return { cityDistrict, neighborhood };
};

export const getPlaceCoreRegionTokens = (place = {}) => {
  const { cityDistrict, neighborhood } = getPlaceRegionParts(place);
  const fallbackRegion = normalizeKoreanToken(place.city || place.state || "");

  if (isLikelyFranchisePlace(place) && cityDistrict && neighborhood) {
    return [cityDistrict, neighborhood];
  }

  return [neighborhood || cityDistrict || fallbackRegion].filter(Boolean);
};

export const getPlaceCoreRegionText = (place = {}) =>
  getPlaceCoreRegionTokens(place).join(" ");

export const getPlaceSeoAreaText = (place = {}) =>
  getPlaceCoreRegionTokens(place)
    .map((token) => token.replace(/시$/u, ""))
    .join(" ");

const flattenTextValues = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.flatMap(flattenTextValues);
  if (typeof value === "object") {
    return [
      value.itemName,
      value.name,
      value.title,
      value.category,
      value.type,
      value.keyword,
    ].flatMap(flattenTextValues);
  }
  return [String(value)];
};

const getExplicitCategoryTexts = (place = {}) =>
  [
    place.representativeFood,
    place.primaryCategory,
    place.seoCategory,
    place.type,
    place.subcategory,
    place.category,
    place.searchKeywords,
    place.kakaoTags,
    place.kTags,
  ].flatMap(flattenTextValues);

const findSpecificCategoryInTexts = (texts) => {
  const haystack = texts.join(" ");

  for (const [keyword, category] of CATEGORY_RULES) {
    if (haystack.includes(keyword)) return category;
  }

  return "";
};

const findFallbackCategoryInTexts = (texts) => {
  const firstCategory = texts
    .flatMap((text) => String(text).split(/[>,\/|·]+/u))
    .map((text) => normalizeKoreanToken(text))
    .find(Boolean);

  return SAFE_CATEGORY_ALIASES.get(firstCategory) || firstCategory || "";
};

export const getPlacePrimaryCategoryText = (place = {}) => {
  const explicitTexts = getExplicitCategoryTexts(place);
  return (
    findSpecificCategoryInTexts(explicitTexts) ||
    findSpecificCategoryInTexts(flattenTextValues(place.name)) ||
    findFallbackCategoryInTexts(explicitTexts)
  );
};

const hasExplicitCategoryText = (place = {}) =>
  getExplicitCategoryTexts(place).some((text) => normalizeKoreanToken(text));

const dedupeSlugParts = (parts) => {
  const seen = new Set();
  return parts.filter((part) => {
    const slug = createKoreanPlaceSlug(part, { fallback: "" });
    const key = compactMeaning(slug);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};


// 주소에 절대 들어가면 안 되는 분류 낱말(청소년유해·주류·유흥).
// 백엔드 placeSensitiveContentPolicy 의 같은 목록을 화면 쪽에도 둔다 — 둘은 붙어 다녀야
// 한다(한쪽만 고치면 canonical 과 실제 주소가 어긋난다).
const SENSITIVE_SLUG_CATEGORIES = new Set([
  "이자카야", "요리주점", "술집", "호프", "포차", "주점", "주류", "맥주", "생맥주",
  "소주방", "간이주점", "가맥", "대포집", "정종", "막걸리", "전통주",
  "바", "와인바", "칵테일바", "위스키바", "펍",
  "노래방", "노래클럽", "단란주점", "룸살롱", "유흥주점", "클럽", "나이트",
  "안마", "마사지", "성인용품", "성인", "키스방", "휴게텔",
]);

const isSensitiveSlugCategory = (category) =>
  SENSITIVE_SLUG_CATEGORIES.has(String(category || "").trim());

export const createPlaceSlug = (placeOrName) => {
  if (typeof placeOrName === "string") {
    return createKoreanPlaceSlug(getPlaceBrandNameForSlug(placeOrName));
  }

  const brand = getPlaceBrandNameForSlug(placeOrName);
  const regionTokens = getPlaceCoreRegionTokens(placeOrName);
  const category = getPlacePrimaryCategoryText(placeOrName);
  const shouldIncludeCategory =
    category &&
    !isSensitiveSlugCategory(category) &&
    (hasExplicitCategoryText(placeOrName) ||
      !compactMeaning(brand).includes(compactMeaning(category)));
  const parts = dedupeSlugParts(
    [brand, ...regionTokens, shouldIncludeCategory ? category : ""].filter(Boolean)
  );

  return createKoreanPlaceSlug(parts.join(" "), { fallback: "place" });
};

const getValueByKeys = (item, keys) => {
  if (!item || typeof item !== "object") return "";

  for (const key of keys) {
    if (item[key] !== null && item[key] !== undefined && item[key] !== "") {
      return item[key];
    }
  }

  return "";
};

export const getPublicId = (item) =>
  typeof item === "string"
    ? normalizeIdSegment(item)
    : normalizeIdSegment(getValueByKeys(item, ID_FALLBACK_KEYS));

export const getPostPublicId = (item) =>
  typeof item === "string"
    ? normalizeIdSegment(item)
    : normalizeIdSegment(item?.publicId);

export const getPlacePublicId = (place) =>
  typeof place === "string"
    ? ""
    : normalizeIdSegment(getValueByKeys(place, PLACE_PUBLIC_ID_KEYS));

export const getPlaceFallbackId = (placeOrId) =>
  typeof placeOrId === "string"
    ? normalizeIdSegment(placeOrId)
    : normalizeIdSegment(getValueByKeys(placeOrId, ID_FALLBACK_KEYS));

const decodePathValue = (value) => {
  try {
    return decodeURIComponent(value);
  } catch (_) {
    return value;
  }
};

const getPlacePathSlugTokenCount = (path, publicId = "") => {
  const decodedPath = decodePathValue(path);
  const slugWithId = decodedPath.split("/place/")[1] || "";
  const slug = publicId
    ? slugWithId.replace(new RegExp(`-?${publicId}$`), "")
    : slugWithId;

  return slug.split("-").filter(Boolean).length;
};

const isUsablePlaceCanonicalPath = (
  canonicalPath,
  publicId = "",
  generatedPath = ""
) => {
  if (!canonicalPath.startsWith("/place/")) return false;
  if (PLACE_OBJECT_ID_DETAIL_PATH_PATTERN.test(canonicalPath)) return false;
  const decodedPath = decodePathValue(canonicalPath);
  if (!HANGUL_PATTERN.test(decodedPath)) return false;
  if (!publicId) return true;

  const hasMatchingPublicId =
    decodedPath.endsWith(`-${publicId}`) || decodedPath.endsWith(`/${publicId}`);
  if (!hasMatchingPublicId) return false;
  if (!generatedPath) return true;

  return (
    getPlacePathSlugTokenCount(canonicalPath, publicId) >=
    getPlacePathSlugTokenCount(generatedPath, publicId)
  );
};

export const buildPostDetailPath = (postOrPublicId) => {
  const publicId = getPostPublicId(postOrPublicId);
  return publicId ? `/post/${encodeURIComponent(publicId)}` : "/play";
};

export const buildPlayDetailPath = (flowOrPublicId) => {
  const publicId = getPostPublicId(flowOrPublicId);
  return publicId ? `/play/${encodeURIComponent(publicId)}` : "/play";
};

export const buildPlaceDetailPath = (placeOrId) => {
  if (!placeOrId) return "/places";

  if (typeof placeOrId === "object") {
    const canonicalPath = generatePlaceCanonicalPath(placeOrId);
    const existingCanonicalPath = stripQueryAndHash(placeOrId.canonicalPath);
    const placePublicId = getPlacePublicId(placeOrId);
    if (
      isUsablePlaceCanonicalPath(
        existingCanonicalPath,
        placePublicId,
        canonicalPath
      )
    ) {
      return existingCanonicalPath;
    }

    if (canonicalPath) return canonicalPath;
  }

  const placeId = getPlaceFallbackId(placeOrId);
  return placeId ? `/place/${encodeURIComponent(placeId)}` : "/places";
};

export const generatePlaceCanonicalPath = (place) => {
  const publicId = getPlacePublicId(place);
  if (!publicId) return "";

  return `/place/${createPlaceSlug(place)}-${encodeURIComponent(publicId)}`;
};

export const buildPlacesCategoryPath = (area, category) => {
  const areaSlug = createSlug(normalizeRouteSegment(area), { fallback: "" });
  const categorySlug = createSlug(normalizeRouteSegment(category), { fallback: "" });

  if (areaSlug && categorySlug) return `/places/${areaSlug}/${categorySlug}`;
  if (areaSlug) return `/places/${areaSlug}`;
  return "/places";
};

export const isVideoMediaType = (mediaType) =>
  String(mediaType || "").toLowerCase().includes("video");

export const isVideoContent = (content) =>
  isVideoMediaType(content?.media?.[0]?.mediaType);

export const buildContentPermanentPath = (content) =>
  isVideoContent(content) ? buildPlayDetailPath(content) : buildPostDetailPath(content);


export const buildCanonicalUrl = (pathOrUrl) => {
  const cleanValue = stripQueryAndHash(pathOrUrl || "/");

  try {
    const url = new URL(cleanValue, SITE_URL);
    if (url.pathname === "/hot" || url.pathname === "/") {
      url.pathname = "/play";
    }
    url.search = "";
    url.hash = "";
    return url.toString();
  } catch {
    const normalizedPath =
      cleanValue === "/hot" || cleanValue === "/" ? "/play" : cleanValue;
    return `${SITE_URL}${normalizedPath.startsWith("/") ? normalizedPath : `/${normalizedPath}`}`;
  }
};

export const buildPostCanonicalUrl = (postOrPublicId) =>
  buildCanonicalUrl(buildPostDetailPath(postOrPublicId));

export const buildPlayCanonicalUrl = (flowOrPublicId) =>
  buildCanonicalUrl(buildPlayDetailPath(flowOrPublicId));

export const buildPlaceCanonicalUrl = (placeOrId) =>
  buildCanonicalUrl(buildPlaceDetailPath(placeOrId));

export const buildPlacesCategoryCanonicalUrl = (area, category) =>
  buildCanonicalUrl(buildPlacesCategoryPath(area, category));

export const normalizeSeoCanonicalUrl = (
  value,
  fallbackPath,
  { legacyPaths = [] } = {}
) => {
  const fallbackCanonical = buildCanonicalUrl(fallbackPath);

  if (!value) return fallbackCanonical;

  try {
    const url = new URL(value, SITE_URL);
    if (legacyPaths.includes(url.pathname) || url.pathname === fallbackPath) {
      return fallbackCanonical;
    }
    return buildCanonicalUrl(url.pathname);
  } catch {
    if (legacyPaths.includes(value) || value === fallbackPath) {
      return fallbackCanonical;
    }
    return buildCanonicalUrl(value);
  }
};
